import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import CustomerSearchSelect from "../components/CustomerSearchSelect";
import "../assets/css/AddLead.css";
import "../assets/css/Issues.css";
import "../assets/css/Permissions.css";
import { getAuthItem } from "../utils/authStorage";
import { formatPascal } from "../utils/formatters";
import { formValueWithMobileSanitize, mobileValidationMessage } from "../utils/phone";
import { leadFieldProfile, projectOptionLabel } from "../utils/leadProfile";

function safeString(v) {
  return v == null ? "" : String(v);
}

function getUpdatedBy() {
  try {
    const userStr = getAuthItem("user");
    if (!userStr) return 1;
    const user = JSON.parse(userStr);
    return user.user_id || user.id || 1;
  } catch {
    return 1;
  }
}

function getId(v) {
  if (v == null || v === "") return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
}

function parseDecimal(v) {
  if (v == null || v === "") return null;
  const s = String(v).trim().replace(/,/g, ".");
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

const REQUIREMENT_TYPES = ["Rent", "Buy", "Lease", "Accommodation"];

function toDateInputValue(value) {
  if (!value) return "";
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

function EditRealestateLead({ leadId, onBack, onUpdateLead }) {
  const updatedBy = getUpdatedBy();

  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    clientId: "",
    tenantClientId: "",
    projectId: "",
    serviceId: "",
    propertyService: "",
    propertyServiceLink: "",
    tenantProjectName: "",
    businessModel: "",
    domainName: "",
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    altPhone: "",
    requirementType: "",
    propertyTypeId: "",
    budgetMin: "",
    budgetMax: "",
    preferredLocation: "",
    propertyLocation: "",
    sharingPreference: "",
    moveInDate: "",
    leadStatusId: "",
    sourceId: "",
    assignedTo: "",
    siteVisit: false,
    isOpportunity: false,
    automationEnabled: false,
    notes: "",
  });

  const [mobileCountryCode, setMobileCountryCode] = useState("+91");
  const savedRef = useRef(null);
  const handleClear = () => {
    if (!savedRef.current) return;
    if (!window.confirm("Discard your changes and restore the saved details?")) return;
    setFormData(savedRef.current.form);
    setMobileCountryCode(savedRef.current.countryCode);
  };
  const [countries, setCountries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tenantClients, setTenantClients] = useState([]);
  const [clientProjects, setClientProjects] = useState([]);
  const [clientServices, setClientServices] = useState([]);
  const [loadingTenantClients, setLoadingTenantClients] = useState(false);
  const [loadingClientAssets, setLoadingClientAssets] = useState(false);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedCustomer = customers.find((c) => String(c.id) === String(formData.clientId));
  const inferredModel = clientProjects.length ? "Project" : clientServices.length ? "Service" : "";
  const profile = leadFieldProfile(
    selectedCustomer?.capturesAllBusinessLeads ? formData.domainName : selectedCustomer?.businessType || formData.domainName,
    formData.businessModel || selectedCustomer?.businessModel || inferredModel
  );
  const offerings = profile.project ? clientProjects : profile.service ? clientServices : [];
  const offeringWord = profile.project ? "projects" : profile.service ? "services" : "projects or services";

  const useProjectMode = !loadingClientAssets && profile.project && clientProjects.length > 0;
  const useServiceMode = !loadingClientAssets && profile.service && clientServices.length > 0;

  const phoneCountryOptions = useMemo(() => {
    return (countries || [])
      .map((c) => ({
        id: c.country_id ?? c.Country_Id ?? c.id,
        code: (c.country_code || c.country_short_code || "").trim(),
        name: c.country_name || c.Country_Name || c.name || "",
      }))
      .filter((x) => x.code);
  }, [countries]);

  // Fetch lead and lookups

  // Fetch active DigiX staff users for Assigned To
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await axiosInstance.get("/users", { params: { status: "Active" } });
        const list = Array.isArray(response.data) ? response.data : [];
        const mapped = list
          .map((u) => ({
            id: u.user_id ?? u.User_Id ?? u.id,
            name: [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.email || `User #${u.user_id}`,
          }))
          .filter((u) => u.id != null)
          .sort((a, b) => a.name.localeCompare(b.name));
        setUsers(mapped);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!leadId) return;
      setLoading(true);
      try {
        const [leadRes, propertyRes, sourceRes, statusRes, countriesRes, customersRes] = await Promise.all([
          axiosInstance.get(`/real-estate-lead/${leadId}`),
          axiosInstance.get("/property-types").catch(() => ({ data: [] })),
          axiosInstance.get("/lead-sources"),
          axiosInstance.get("/lead-statuses"),
          axiosInstance.get("/countries").catch(() => ({ data: [] })),
          axiosInstance.get("/clients").catch(() => ({ data: [] })),
        ]);

        const lead =
          leadRes.data?.real_estate_lead ||
          leadRes.data?.realEstateLead ||
          leadRes.data;

        if (lead && typeof lead === "object") {
          const loaded = {
            clientId: lead.client_id != null ? String(lead.client_id) : (lead.tenant_id != null ? String(lead.tenant_id) : ""),
            tenantClientId: lead.Tenant_Client_Id != null ? String(lead.Tenant_Client_Id) : "",
            projectId: lead.Tenant_Project_Id != null ? String(lead.Tenant_Project_Id) : "",
            serviceId: "",
            propertyService: safeString(
              lead.Property_Service ??
              lead.Property_Service_Name ??
              lead.Service_Property_Name
            ),
            propertyServiceLink: safeString(lead.Property_Service_Link),
            tenantProjectName: safeString(lead.Tenant_Project_Name),
            businessModel: safeString(lead.Business_Model),
            domainName: safeString(lead.Domain_Name),
            firstName: safeString(lead.First_Name),
            lastName: safeString(lead.Last_Name),
            email: safeString(lead.Email),
            mobileNumber: safeString(lead.Mobile_Number),
            altPhone: safeString(lead.Alt_Phone).replace(/\D/g, "").slice(0, 10),
            requirementType: lead.Requirement_Type || "",
            propertyTypeId: lead.Property_Type_Id ?? "",
            budgetMin: lead.Budget_Min != null ? String(lead.Budget_Min) : "",
            budgetMax: lead.Budget_Max != null ? String(lead.Budget_Max) : "",
            preferredLocation: safeString(lead.Preferred_Location),
            propertyLocation: safeString(lead.Property_Location),
            sharingPreference: safeString(lead.Sharing_Preference),
            moveInDate: toDateInputValue(lead.Move_In_Date),
            leadStatusId: lead.Lead_Status_Id ?? "",
            sourceId: lead.Source_Id ?? "",
            assignedTo: (lead.Assigned_To ?? lead.assigned_to) != null && (lead.Assigned_To ?? lead.assigned_to) !== "" ? String(lead.Assigned_To ?? lead.assigned_to) : "",
            siteVisit: !!(lead.Site_Visit ?? lead.site_visit),
            isOpportunity: !!(lead.Is_Opportunity ?? lead.is_opportunity),
            automationEnabled: !!(lead.Automation_Enabled ?? lead.automation_enabled),
            notes: safeString(lead.Notes),
          };
          savedRef.current = { form: loaded, countryCode: safeString(lead.Country_Code) || "+91" };
          setFormData(loaded);
          if (lead.Country_Code) {
            setMobileCountryCode(safeString(lead.Country_Code) || "+91");
          }
        }

        const rawCustomers = Array.isArray(customersRes.data)
          ? customersRes.data
          : Array.isArray(customersRes.data?.results)
            ? customersRes.data.results
            : Array.isArray(customersRes.data?.data)
              ? customersRes.data.data
              : [];
        setCustomers(
          rawCustomers
            .map((item) => {
              const c = item.client || item;
              const id = c.client_id ?? c.client_id ?? c.id;
              const first = c.First_Name || "";
              const last = c.Last_Name || "";
              return {
                id,
                name: `${first} ${last}`.trim() || `Customer #${id}`,
                phone: c.Phone || "",
                email: c.Email || "",
                capturesAllBusinessLeads: !!c.Captures_All_Business_Leads,
                businessModel: c.Business_Model || "",
                businessType: c.Domain_Name || "",
              };
            })
            .filter((c) => c.id != null)
        );

        const rawCountries = Array.isArray(countriesRes?.data)
          ? countriesRes.data
          : Array.isArray(countriesRes?.data?.data)
            ? countriesRes.data.data
            : [];
        setCountries(rawCountries);

        const rawPropertyTypes = Array.isArray(propertyRes.data)
          ? propertyRes.data
          : Array.isArray(propertyRes.data?.data)
            ? propertyRes.data.data
            : [];
        setPropertyTypes(
          rawPropertyTypes
            .map((p) => ({
              id: p.Property_Type_Id ?? p.property_type_id ?? p.id,
              name: p.Property_Type_Name ?? p.property_type_name ?? p.name ?? "",
            }))
            .filter((p) => p.id != null && String(p.name).trim() !== "")
        );

        const rawSources = Array.isArray(sourceRes.data)
          ? sourceRes.data
          : Array.isArray(sourceRes.data?.data)
            ? sourceRes.data.data
            : [];
        setSources(
          rawSources
            .map((s) => ({
              id: s.Source_Id ?? s.source_id ?? s.id,
              name: s.Source_Name ?? s.source_name ?? s.name ?? "",
            }))
            .filter((s) => s.id != null && String(s.name).trim() !== "")
        );

        const rawStatuses = Array.isArray(statusRes.data)
          ? statusRes.data
          : Array.isArray(statusRes.data?.data)
            ? statusRes.data.data
            : [];
        setStatuses(
          rawStatuses
            .map((s) => ({
              id: s.Status_Id ?? s.status_id ?? s.id,
              name: s.Status ?? s.status ?? s.name ?? "",
            }))
            .filter((s) => s.id != null && String(s.name).trim() !== "")
        );
      } catch (err) {
        console.error("Error loading real estate lead:", err);
        alert("Failed to load real estate lead details.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [leadId]);

  useEffect(() => {
    let active = true;
    const tenantId = getId(formData.clientId);
    if (!tenantId) {
      setTenantClients([]);
      return undefined;
    }
    setLoadingTenantClients(true);
    (async () => {
      try {
        const res = await axiosInstance.get("/tenant-clients/", {
          params: { tenant_id: tenantId },
        });
        const raw = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        if (!active) return;
        setTenantClients(
          raw
            .map((row) => ({
              id: row.Tenant_Client_Id ?? row.tenant_client_id ?? row.id,
              name: row.Client_Name || row.client_name || `Client #${row.Tenant_Client_Id}`,
              status: row.Status || row.status || "Active",
            }))
            .filter((c) => c.id != null && String(c.status).toLowerCase() !== "inactive")
        );
      } catch (err) {
        console.error("Error fetching tenant clients:", err);
        if (active) setTenantClients([]);
      } finally {
        if (active) setLoadingTenantClients(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [formData.clientId]);

  useEffect(() => {
    let active = true;
    const tenantClientId = getId(formData.tenantClientId);
    if (!tenantClientId) {
      setClientProjects([]);
      setClientServices([]);
      return undefined;
    }
    setLoadingClientAssets(true);
    (async () => {
      try {
        const [projectsRes, servicesRes] = await Promise.all([
          axiosInstance.get("/tenant-projects/", {
            params: { tenant_client_id: tenantClientId },
          }),
          axiosInstance.get("/tenant-client-services/", {
            params: { tenant_client_id: tenantClientId },
          }),
        ]);
        const projectsRaw = Array.isArray(projectsRes.data?.data)
          ? projectsRes.data.data
          : Array.isArray(projectsRes.data)
            ? projectsRes.data
            : [];
        const servicesRaw = Array.isArray(servicesRes.data?.data)
          ? servicesRes.data.data
          : Array.isArray(servicesRes.data)
            ? servicesRes.data
            : [];
        if (!active) return;
        const projects = projectsRaw
          .map((row) => ({
            id: row.Tenant_Project_Id ?? row.tenant_project_id ?? row.id,
            name: row.Project_Name || row.project_name || `Project #${row.Tenant_Project_Id}`,
            label: projectOptionLabel(
              row.Project_Name || row.project_name || `Project #${row.Tenant_Project_Id}`,
              row.Address
            ),
            status: row.Status || row.status || "Active",
          }))
          .filter((p) => p.id != null && String(p.status).toLowerCase() !== "inactive");
        const services = servicesRaw
          .map((row) => ({
            id: row.Tenant_Client_Service_Id ?? row.tenant_client_service_id ?? row.id,
            name: row.Service_Name || row.service_name || `Service #${row.Tenant_Client_Service_Id}`,
            status: row.Status || row.status || "Active",
          }))
          .filter((s) => s.id != null && String(s.status).toLowerCase() !== "inactive");
        setClientProjects(projects);
        setClientServices(services);

        setFormData((prev) => {
          if (prev.projectId || prev.serviceId) return prev;
          const projectName = (prev.tenantProjectName || prev.propertyService || "").trim().toLowerCase();
          const serviceName = (prev.propertyService || "").trim().toLowerCase();
          if (projects.length && projectName) {
            const match = projects.find((p) => String(p.name).trim().toLowerCase() === projectName);
            if (match) {
              return {
                ...prev,
                projectId: String(match.id),
                serviceId: "",
                tenantProjectName: match.name,
                propertyService: match.name,
              };
            }
          }
          if (!projects.length && services.length && serviceName) {
            const match = services.find((s) => String(s.name).trim().toLowerCase() === serviceName);
            if (match) {
              return {
                ...prev,
                serviceId: String(match.id),
                projectId: "",
                tenantProjectName: "",
                propertyService: match.name,
              };
            }
          }
          return prev;
        });
      } catch (err) {
        console.error("Error fetching client projects/services:", err);
        if (active) {
          setClientProjects([]);
          setClientServices([]);
        }
      } finally {
        if (active) setLoadingClientAssets(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [formData.tenantClientId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : formValueWithMobileSanitize(name, value);
    setFormData((p) => ({ ...p, [name]: nextValue }));
  };

  const handleSelectClient = (clientId) => {
    const id = String(clientId || "");
    setFormData((p) => ({
      ...p,
      clientId: id,
      tenantClientId: "",
      projectId: "",
      serviceId: "",
      propertyService: "",
      tenantProjectName: "",
    }));
    setTenantClients([]);
    setClientProjects([]);
    setClientServices([]);
  };

  const handleSelectTenantClient = (e) => {
    const id = e.target.value;
    setFormData((p) => ({
      ...p,
      tenantClientId: id,
      projectId: "",
      serviceId: "",
      propertyService: "",
      tenantProjectName: "",
    }));
    setClientProjects([]);
    setClientServices([]);
  };

  const handleSelectProject = (e) => {
    const id = e.target.value;
    const project = clientProjects.find((p) => String(p.id) === String(id));
    const name = project?.name || "";
    setFormData((p) => ({
      ...p,
      projectId: id,
      serviceId: "",
      tenantProjectName: name,
      propertyService: name,
      ...(id
        ? {}
        : { sharingPreference: "", moveInDate: "" }),
    }));
  };

  const handleSelectService = (e) => {
    const id = e.target.value;
    const service = clientServices.find((s) => String(s.id) === String(id));
    const name = service?.name || "";
    setFormData((p) => ({
      ...p,
      serviceId: id,
      projectId: "",
      tenantProjectName: "",
      propertyService: name,
      sharingPreference: "",
      moveInDate: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = [
      ["clientId", formData.clientId],
      ["tenantClientId", formData.tenantClientId],
      ["firstName", formData.firstName],
      ["mobileNumber", formData.mobileNumber],
      ["sourceId", formData.sourceId],
      ["leadStatusId", formData.leadStatusId],
    ];
    if (useProjectMode) requiredFields.push(["projectId", formData.projectId]);
    else if (useServiceMode) requiredFields.push(["serviceId", formData.serviceId]);

    const missing = requiredFields
      .filter(([, v]) => !safeString(v).trim())
      .map(([k]) => k);
    if (missing.length) {
      alert(
        `Please select tenant, ${profile.ownerLabel.toLowerCase()}, ${profile.offeringLabel.toLowerCase()}, and fill all required fields.`
      );
      return;
    }
    if (formData.tenantClientId && !loadingClientAssets && !offerings.length) {
      alert(`Selected ${profile.ownerLabel.toLowerCase()} has no ${offeringWord}.`);
      return;
    }
    const mobileError = mobileValidationMessage(formData.mobileNumber, { required: true, label: "Mobile number" });
    if (mobileError) {
      alert(mobileError);
      return;
    }
    const altError = mobileValidationMessage(formData.altPhone, { label: "Alternate phone" });
    if (altError) {
      alert(altError);
      return;
    }

    const budgetMin = parseDecimal(formData.budgetMin);
    const budgetMax = parseDecimal(formData.budgetMax);
    if (formData.budgetMin && (budgetMin == null || budgetMin < 0)) {
      alert("Please enter a valid number for Budget Min.");
      return;
    }
    if (formData.budgetMax && (budgetMax == null || budgetMax < 0)) {
      alert("Please enter a valid number for Budget Max.");
      return;
    }
    if (budgetMin != null && budgetMax != null && budgetMin > budgetMax) {
      alert("Budget Min cannot be greater than Budget Max.");
      return;
    }

    const updatedByNumber = getId(updatedBy) ?? 1;

    const payload = {
      client_id: getId(formData.clientId),
      Property_Service: formData.propertyService.trim() || null,
      Property_Service_Link: profile.showLink ? formData.propertyServiceLink.trim() || null : null,
      Tenant_Project_Name: formData.tenantProjectName.trim() || null,
      Tenant_Project_Id: useProjectMode && formData.projectId ? Number(formData.projectId) : null,
      First_Name: formatPascal(formData.firstName.trim()),
      Last_Name: formData.lastName.trim() ? formatPascal(formData.lastName.trim()) : null,
      Email: formData.email.trim() || null,
      Country_Code: mobileCountryCode.trim() || "+91",
      Mobile_Number: formData.mobileNumber.trim(),
      Alt_Phone: formData.altPhone.trim() || null,
      Business_Model: profile.model || null,
      Requirement_Type: profile.showRequirementType ? formData.requirementType || null : null,
      Property_Type_Id: profile.showPropertyDetails ? getId(formData.propertyTypeId) : null,
      Budget_Min: profile.showBudget && budgetMin != null ? budgetMin : null,
      Budget_Max: profile.showBudget && budgetMax != null ? budgetMax : null,
      Preferred_Location: profile.showPreferredLocation ? formData.preferredLocation.trim() || null : null,
      Property_Location: profile.showPropertyDetails ? formData.propertyLocation.trim() || null : null,
      Sharing_Preference: profile.showPropertyDetails ? formData.sharingPreference.trim() || null : null,
      Move_In_Date:
        profile.showPropertyDetails || profile.showPreferredDate ? formData.moveInDate || null : null,
      Lead_Status_Id: getId(formData.leadStatusId),
      Source_Id: getId(formData.sourceId),
      Assigned_To: getId(formData.assignedTo),
      Site_Visit: profile.showSiteVisit && !!formData.siteVisit,
      Is_Opportunity: !!formData.isOpportunity,
      Automation_Enabled: !!formData.automationEnabled,
      Notes: formData.notes.trim() || null,
      Updated_By: updatedByNumber,
    };

    setSubmitting(true);
    try {
      const response = await axiosInstance.put(
        `/update-real-estate-lead/${leadId}`,
        payload
      );
      const msg =
        response.data?.message ||
        response.data?.error ||
        "Real estate lead updated successfully.";

      alert(msg);

      if (response.data && onUpdateLead) {
        onUpdateLead(response.data);
      }
      if (onBack) onBack();
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to update real estate lead.";
      console.error("Error updating real estate lead:", err);
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="addlead-container">
        <div style={{ padding: 24, textAlign: "center" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="addlead-container">
      <button type="button" className="back-link" onClick={onBack}>
        <FaArrowLeft /> Back
      </button>
      <div className="issues-main-card">
      <form className="addlead-form re-grouped-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h3 className="re-form-section-title">Lead Contact</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>
                Mobile Number <span className="required">*</span>
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={mobileCountryCode}
                  onChange={(e) => setMobileCountryCode(e.target.value)}
                  style={{ maxWidth: 120 }}
                  required
                >
                  <option value="+91">+91</option>
                  {phoneCountryOptions.map((c) => (
                    <option key={c.id ?? c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  inputMode="numeric"
                  maxLength={10}
                  pattern="[0-9]{10}"
                  title="Enter exactly 10 digits"
                  required
                  style={{ flex: 1 }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Alt Phone</label>
              <input
                type="tel"
                name="altPhone"
                value={formData.altPhone}
                onChange={handleChange}
                inputMode="numeric"
                maxLength={10}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3 className="re-form-section-title">Tenant Assignment</h3>
          <div className="form-grid">
            <div className="form-group re-field-tenant">
              <label>
                Tenant <span className="required">*</span>
              </label>
              <CustomerSearchSelect
                customers={customers}
                value={formData.clientId}
                onChange={handleSelectClient}
              />
            </div>

            <div className="form-group">
              <label>
                {profile.ownerLabel === "Client" ? "Tenant client" : profile.ownerLabel} <span className="required">*</span>
              </label>
              <select
                name="tenantClientId"
                value={formData.tenantClientId}
                onChange={handleSelectTenantClient}
                disabled={!formData.clientId || loadingTenantClients}
                required
              >
                <option value="">
                  {!formData.clientId
                    ? "Select tenant first"
                    : loadingTenantClients
                      ? "Loading clients..."
                      : tenantClients.length
                        ? `Select ${profile.ownerLabel === "Client" ? "tenant client" : profile.ownerLabel.toLowerCase()}`
                        : "None for this tenant"}
                </option>
                {tenantClients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {formData.tenantClientId ? (
              loadingClientAssets ? (
                <div className="form-group re-project-service-slot">
                  <label>{profile.offeringLabel}</label>
                  <select disabled value="">
                    <option value="">Loading…</option>
                  </select>
                </div>
              ) : useProjectMode ? (
                <div className="form-group re-project-service-slot">
                  <label>
                    Project <span className="required">*</span>
                  </label>
                  <select
                    name="projectId"
                    value={formData.projectId}
                    onChange={handleSelectProject}
                    required
                  >
                    <option value="">Select project</option>
                    {clientProjects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : useServiceMode ? (
                <div className="form-group re-project-service-slot">
                  <label>
                    Service <span className="required">*</span>
                  </label>
                  <select
                    name="serviceId"
                    value={formData.serviceId}
                    onChange={handleSelectService}
                    required
                  >
                    <option value="">Select service</option>
                    {clientServices.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group re-project-service-slot">
                  <label>{profile.offeringLabel}</label>
                  <select disabled value="">
                    <option value="">No {offeringWord} for this client</option>
                  </select>
                </div>
              )
            ) : (
              <div className="form-group re-project-service-slot">
                <label>{profile.offeringLabel}</label>
                <select disabled value="">
                  <option value="">
                    Select {profile.ownerLabel === "Client" ? "tenant client" : profile.ownerLabel.toLowerCase()} first
                  </option>
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="form-section" hidden={!profile.showDetails}>
          <h3 className="re-form-section-title">{profile.detailsTitle}</h3>
          <div className="form-grid">
            {profile.showPropertyDetails ? (
              <div className="form-group">
                <label>Property Location</label>
                <input
                  type="text"
                  name="propertyLocation"
                  value={formData.propertyLocation}
                  onChange={handleChange}
                  placeholder="Enter property location"
                />
              </div>
            ) : null}

            <div className="form-group">
              <label>{profile.linkLabel}</label>
              <input
                type="url"
                name="propertyServiceLink"
                value={formData.propertyServiceLink}
                onChange={handleChange}
                placeholder="https://example.com/..."
                maxLength={2048}
              />
            </div>

            {profile.showPropertyDetails ? (
              <div className="form-group">
                <label>Property Type</label>
                <select
                  name="propertyTypeId"
                  value={formData.propertyTypeId}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  {propertyTypes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {profile.showRequirementType ? (
              <div className="form-group">
                <label>Requirement Type</label>
                <select
                  name="requirementType"
                  value={formData.requirementType}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  {REQUIREMENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {profile.showBudget ? (
              <>
                <div className="form-group">
                  <label>Budget Min</label>
                  <input
                    type="text"
                    name="budgetMin"
                    value={formData.budgetMin}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Budget Max</label>
                  <input
                    type="text"
                    name="budgetMax"
                    value={formData.budgetMax}
                    onChange={handleChange}
                  />
                </div>
              </>
            ) : null}

            {profile.showPreferredLocation ? (
              <div className="form-group">
                <label>Preferred Location</label>
                <input
                  type="text"
                  name="preferredLocation"
                  value={formData.preferredLocation}
                  onChange={handleChange}
                />
              </div>
            ) : null}

            {profile.showPropertyDetails ? (
              <div className="form-group">
                <label>Sharing Preference</label>
                <input
                  type="text"
                  name="sharingPreference"
                  value={formData.sharingPreference}
                  onChange={handleChange}
                  placeholder="e.g. 2 sharing, 2 BHK"
                  maxLength={100}
                />
              </div>
            ) : null}

            {profile.showPropertyDetails || profile.showPreferredDate ? (
              <div className="form-group">
                <label>{profile.showPropertyDetails ? "Move-in Date" : "Preferred Date"}</label>
                <input
                  type="date"
                  name="moveInDate"
                  value={formData.moveInDate}
                  onChange={handleChange}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="form-section">
          <h3 className="re-form-section-title">Lead Pipeline</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                Source <span className="required">*</span>
              </label>
              <select
                name="sourceId"
                value={formData.sourceId}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Status <span className="required">*</span>
              </label>
              <select
                name="leadStatusId"
                value={formData.leadStatusId}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Assigned To</label>
              <select
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleChange}
                disabled={loadingUsers}
              >
                {loadingUsers && <option value="">Loading users...</option>}
                {!loadingUsers && <option value="">Select user</option>}
                {users.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="re-pipeline-flags">
            {profile.showSiteVisit ? (
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="siteVisit"
                  checked={!!formData.siteVisit}
                  onChange={handleChange}
                />
                <span>Site Visit</span>
              </label>
            ) : null}
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isOpportunity"
                checked={!!formData.isOpportunity}
                onChange={handleChange}
              />
              <span>Opportunity</span>
            </label>
            <label className="automation-switch">
              <input
                type="checkbox"
                name="automationEnabled"
                checked={!!formData.automationEnabled}
                onChange={handleChange}
              />
              <span className="automation-switch-track" aria-hidden="true">
                <span className="automation-switch-thumb" />
              </span>
              <span>Automation</span>
            </label>
          </div>
        </div>

        <div className="form-section">
          <h3 className="re-form-section-title">Notes</h3>
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                aria-label="Notes"
              />
            </div>
          </div>
        </div>

        <div className="form-actions re-form-actions">
          <button type="button" className="btn-clear-lead" onClick={handleClear} disabled={submitting}>
            Clear
          </button>
          <button type="submit" className="btn-save-lead" disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

export default EditRealestateLead;
